import { useCallback, useEffect, useState } from 'react';

import { fetchJson } from '@api/ApiHelper';

import useDebounce from '@components/common/custom_hooks/useDebounce';

import {
  PeoplePageResponse,
  PeoplePage,
  PeopleFilterType,
} from '@components/people_tags/customPeopleTagTypes';

import { usePeopleManagementContext } from '../PeopleManagementContext';

// API response type from the identities endpoint
interface IdentityApiResponse {
  id: string;
  boundary_id: string;
  name: string;
  metadata?: string; // JSON string containing { hide: boolean }
  created_at: string;
  updated_at: string;
  face_count: number;
  identity_thumbnail_url: string;
}

const parseHiddenFromMetadata = (metadata?: string): boolean => {
  if (!metadata) {
    return false;
  }
  try {
    const parsed = JSON.parse(metadata) as { hide?: boolean };
    return !!parsed.hide;
  } catch {
    return false;
  }
};

export interface UseFetchPeopleHubDataReturn {
  initialLoadingState: boolean;
  peopleData: PeoplePage[];
  peopleDataError: boolean;
  peopleDataLoading: boolean;
  refetchPeopleData: () => Promise<void>;
  allCount: number;
  namedCount: number;
  unnamedCount: number;
  totalCount: number;
  bfProcessed: boolean;
  totalPages: number;
  currentPage: number;
}

const ITEMS_PER_PAGE = 24;

export const useFetchPeopleHubData = (): UseFetchPeopleHubDataReturn => {
  const { state } = usePeopleManagementContext();
  const [responseData, setResponseData] = useState<PeoplePageResponse | null>(null);
  const [peopleDataLoading, setPeopleDataLoading] = useState<boolean>(true);
  const [initialLoadingState, setInitialLoadingState] = useState<boolean>(true);
  const [peopleDataError, setPeopleDataError] = useState<boolean>(false);

  // Debounce search to avoid excessive API calls
  // Trim trailing spaces so "Test" and "Test " are treated the same
  const debouncedSearch = useDebounce({ value: state.search.trimEnd(), delay: 600 });

  // Build query params from state
  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();

    params.append('page', state.page.toString());
    params.append('per', ITEMS_PER_PAGE.toString());

    if (debouncedSearch.trim()) {
      params.append('search', debouncedSearch.trim());
    }

    if (state.sort) {
      params.append('sort', state.sort);
    }

    if (state.filter !== PeopleFilterType.All) {
      params.append('filter', state.filter);
    }

    if (!state.showHiddenPeople) {
      params.append('exclude_hidden', 'true');
    }

    return params;
  }, [state.page, state.sort, state.filter, state.showHiddenPeople, debouncedSearch]);

  const loadPeopleData = useCallback(async (): Promise<void> => {
    try {
      setPeopleDataLoading(true);
      setPeopleDataError(false);

      // Get brandfolder key from global BFG object
      const brandfolderKey = BFG.brandfolder_key;

      if (!brandfolderKey) {
        throw new Error('Brandfolder key not found');
      }

      const queryParams = buildQueryParams();
      const url = `/api/v4/private/people_tagging/brandfolders/${brandfolderKey}/identities?${queryParams.toString()}`;

      // Make API call to fetch identities with query params
      const response = await fetchJson<{
        data: IdentityApiResponse[];
        meta: {
          current_page: number;
          next_page: number | null;
          prev_page: number | null;
          total_pages: number;
          total_count: number;
          named_count: number;
          unnamed_count: number;
          bf_processed: boolean;
        };
      }>({
        url,
        method: 'GET',
      });

      // Transform API identities to PeoplePage format
      const transformedData: PeoplePage[] = response.data.map((identity) => ({
        thumbnail_url: identity.identity_thumbnail_url || '',
        person_id: identity.id,
        name: identity.name || '',
        isHidden: parseHiddenFromMetadata(identity.metadata),
        created_at: identity.created_at,
        face_count: identity.face_count,
      }));

      const transformedResponse: PeoplePageResponse = {
        data: transformedData,
        meta: response.meta,
      };

      setResponseData(transformedResponse);
      setPeopleDataError(false);
    } catch (error) {
      console.error('Error fetching people data:', error);
      setResponseData(null);
      setPeopleDataError(true);
    } finally {
      setPeopleDataLoading(false);
      setInitialLoadingState(false);
    }
  }, [buildQueryParams]);

  // Re-fetch data when any relevant state changes
  useEffect(() => {
    loadPeopleData();
  }, [loadPeopleData]);

  // Calculate counts from meta response
  const allCount = responseData?.meta ? responseData.meta.named_count + responseData.meta.unnamed_count : 0;
  const namedCount = responseData?.meta.named_count ?? 0;
  const unnamedCount = responseData?.meta.unnamed_count ?? 0;

  return {
    initialLoadingState,
    peopleData: responseData?.data || [],
    peopleDataError,
    peopleDataLoading,
    refetchPeopleData: loadPeopleData,
    allCount,
    namedCount,
    unnamedCount,
    totalCount: responseData?.meta.total_count ?? 0,
    bfProcessed: responseData?.meta.bf_processed ?? false,
    totalPages: responseData?.meta.total_pages ?? 0,
    currentPage: responseData?.meta.current_page ?? state.page,
  };
};
